import { useState } from 'react';
import { Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem, Alert, FormControlLabel, Switch, Chip } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { itemsApi, type InventoryItem, type ItemInput } from '../../api/inventory.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const empty: ItemInput = { name: '', uom: 'nos', gstPercent: 0, minStock: 0, maxStock: 0, reorderLevel: 0, isAsset: false, serialTracked: false, status: 'active' };

export function ItemsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [lowStock, setLowStock] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [form, setForm] = useState<ItemInput>(empty);
  const [deleting, setDeleting] = useState<InventoryItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['inventory-items', page, limit, search, lowStock], queryFn: () => itemsApi.list({ page, limit, search: search || undefined, lowStock: lowStock || undefined }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['inventory-items'] });

  const save = useMutation({
    mutationFn: () => (editing ? itemsApi.update(editing.id, form) : itemsApi.create(form)),
    onSuccess: () => { setOpen(false); invalidate(); },
    onError: (e) => setError(extractApiError(e)),
  });
  const del = useMutation({ mutationFn: (id: number) => itemsApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setError(extractApiError(e)); setDeleting(null); } });

  const openNew = () => { setEditing(null); setForm(empty); setError(null); setOpen(true); };
  const openEdit = (i: InventoryItem) => {
    setEditing(i);
    setForm({ name: i.name, category: i.category ?? undefined, subCategory: i.subCategory ?? undefined, brand: i.brand ?? undefined, oem: i.oem ?? undefined, uom: i.uom, hsnCode: i.hsnCode ?? undefined, gstPercent: i.gstPercent, minStock: i.minStock, maxStock: i.maxStock, reorderLevel: i.reorderLevel, isAsset: i.isAsset, serialTracked: i.serialTracked, barcode: i.barcode ?? undefined, status: i.status as 'active' | 'inactive' });
    setError(null); setOpen(true);
  };
  const set = <K extends keyof ItemInput>(k: K, v: ItemInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const columns: Column<InventoryItem>[] = [
    { key: 'code', label: 'Code', width: 110 },
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Category', render: (r) => r.category ?? '—' },
    { key: 'brand', label: 'Brand', render: (r) => r.brand ?? '—' },
    { key: 'uom', label: 'UOM', width: 70 },
    { key: 'reorderLevel', label: 'Reorder', align: 'right', render: (r) => r.reorderLevel },
    { key: 'type', label: 'Type', render: (r) => <Stack direction="row" spacing={0.5}><Chip size="small" label={r.isAsset ? 'Asset' : 'Consumable'} color={r.isAsset ? 'secondary' : 'default'} variant="outlined" />{r.serialTracked && <Chip size="small" label="Serial" color="info" variant="outlined" />}</Stack> },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader title="Item Master" subtitle="Materials & assets — category, brand, OEM, UOM, HSN/GST and stock policy." action={hasPermission('inventory_item.create') && <Button startIcon={<Add />} variant="contained" onClick={openNew}>New Item</Button>} />

      <TableToolbar
        search={search} onSearch={(v) => { setSearch(v); setPage(1); }} searchPlaceholder="Search code, name, brand, HSN…"
        filters={<FormControlLabel control={<Switch checked={lowStock} onChange={(e) => { setLowStock(e.target.checked); setPage(1); }} />} label="Low stock only" />}
      />

      <DataTable
        columns={columns} rows={list.data?.data ?? []} loading={list.isLoading}
        page={page} limit={limit} total={list.data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={setLimit} rowKey={(r) => r.id}
        emptyTitle="No items"
        actions={(r) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {hasPermission('inventory_item.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('inventory_item.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </Stack>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editing ? 'Edit Item' : 'New Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField label="Name" value={form.name} onChange={(e) => set('name', e.target.value)} fullWidth />
              <TextField label="UOM" value={form.uom ?? ''} onChange={(e) => set('uom', e.target.value)} sx={{ width: 120 }} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Category" value={form.category ?? ''} onChange={(e) => set('category', e.target.value)} fullWidth />
              <TextField label="Sub Category" value={form.subCategory ?? ''} onChange={(e) => set('subCategory', e.target.value)} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Brand" value={form.brand ?? ''} onChange={(e) => set('brand', e.target.value)} fullWidth />
              <TextField label="OEM" value={form.oem ?? ''} onChange={(e) => set('oem', e.target.value)} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="HSN Code" value={form.hsnCode ?? ''} onChange={(e) => set('hsnCode', e.target.value)} fullWidth />
              <TextField label="GST %" type="number" value={form.gstPercent ?? 0} onChange={(e) => set('gstPercent', Number(e.target.value))} fullWidth />
              <TextField label="Barcode / QR" value={form.barcode ?? ''} onChange={(e) => set('barcode', e.target.value)} fullWidth />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField label="Min Stock" type="number" value={form.minStock ?? 0} onChange={(e) => set('minStock', Number(e.target.value))} fullWidth />
              <TextField label="Max Stock" type="number" value={form.maxStock ?? 0} onChange={(e) => set('maxStock', Number(e.target.value))} fullWidth />
              <TextField label="Reorder Level" type="number" value={form.reorderLevel ?? 0} onChange={(e) => set('reorderLevel', Number(e.target.value))} fullWidth />
            </Stack>
            <Stack direction="row" spacing={3} alignItems="center">
              <FormControlLabel control={<Switch checked={form.isAsset ?? false} onChange={(e) => set('isAsset', e.target.checked)} />} label="Asset (vs consumable)" />
              <FormControlLabel control={<Switch checked={form.serialTracked ?? false} onChange={(e) => set('serialTracked', e.target.checked)} />} label="Serial tracked" />
              <TextField select label="Status" value={form.status ?? 'active'} onChange={(e) => set('status', e.target.value as 'active' | 'inactive')} sx={{ width: 160 }}>
                <MenuItem value="active">Active</MenuItem><MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.name || save.isPending} onClick={() => save.mutate()}>Save</Button></DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleting)} title="Delete item" message={`Delete ${deleting?.name}? Items with stock movements cannot be deleted.`} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && del.mutate(deleting.id)} />
    </>
  );
}
